import React from 'react';

import './RMDDisclosure.scss';

import Button from '../../Shared/Button/Button';
import TRP from '../../Shared/TRP/TRP';
import { ButtonType } from '../../Shared/Button/types';
import styles from '../../Shared/APPText/APPText.module.scss';

const RMDDisclosure = ({ closeRMDDisclosure }) => (
  <div className="rmd-disclosure-container">
    <h1 className="rmd-disclosure-heading"> Important Disclosure </h1>
    <div className="rmd-disclosure-title">Calculation Method:</div>
    <div className="rmd-disclosure-description">
      <div>
        The RMD for a year is determined by dividing the previous year-end’s
        fair market value (on December 31) by the applicable distribution period
        (from the appropriate life expectancy table). The year-end value may
        need to be adjusted for transfers or rollovers in transit.
      </div>
    </div>
    <div className="rmd-disclosure-title">Disclosure:</div>
    <ul className="rmd-disclosure-events-list">
      <li>
        The RMD calculation is an estimate. You are responsible for calculating
        and withdrawing the RMD amount under Internal Revenue Code 401(a)(9) and
        liable should the correct amount fail to be received in any calendar
        year. For brokerage accounts, if securities must be sold in order to
        accommodate a cash distribution from the sweep account, ensure your
        trades are placed in time for settlement to take place before the RMD
        date.
      </li>
      <li>
        Generally, starting when you’re 73 (72 if you turned 72 between January
        1, 2020 and December 31, 2022 or 70 ½ if you turned 70 ½ before January
        1, 2020), you’re required to take mandatory withdrawals from your
        Traditional, Rollover, SEP, and SIMPLE IRAs, and employer-sponsored
        retirement plans. This annual withdrawal is known as a required minimum
        distribution (RMD).
      </li>
      <li>
        Roth IRAs do not require withdrawals until after the death of the owner.
        Please note, different RMD rules apply to inherited retirement accounts.
        For questions relating to inherited retirement accounts, including IRAs,
        please speak to your legal or tax professional.
      </li>
      <li>
        Non-IRA retirement plans, such as 401(k) or 403(b) plans, may have
        different deadlines. Please speak with your plan administrator for
        details.
      </li>
      <li>
        Request your distribution no later than one week prior to the
        deadline(s) to be sure that the RMD occurs before the due date.
        Otherwise, if you don't meet your distribution deadline, the RMD may be
        considered late and could have significant tax consequences and
        penalties.
      </li>
    </ul>

    <div className="rmd-disclosure-title">Assumptions:</div>
    <ul className="rmd-disclosure-events-list">
      <li>
        Calculation of the RMD amount commencing during your lifetime is based
        on assets in retirement accounts held at T. Rowe Price on 12/31 of the
        prior year.{' '}
        <b>
          The calculation does not include amounts that may be due from any
          employer-sponsored retirement plan accounts you may have with T. Rowe
          Price or at other financial institutions.
        </b>
      </li>
      <li>
        The calculation will not include amounts that may require an adjustment
        (see above).
      </li>
      <li>
        For Mutual Fund, Brokerage, ActivePlus Portfolios
        <sup className={styles.sup}>®</sup>, and Retirement Advisory Service
        accounts, all calculations are based on IRS Pub 590-B Table III (Uniform
        Lifetime) unless you designate your spouse as the sole primary
        beneficiary and your spouse is greater than 10 years younger than you.
        If so, and we have your spouse's date of birth on file (review Account
        Access for verification), calculations are based on IRS Pub 590-B Table
        II (Joint Life and Last Survivor Expectancy).
      </li>
      <li>
        If your spouse is the sole primary beneficiary on January 1 of any year,{' '}
        <TRP /> assumes:
        <ul className="rmd-disclosure-events-list">
          <li>they are your spouse for the entire year,</li>
          <li>they are alive for the entire year, and</li>
          <li>they are the sole primary beneficiary for the entire year.</li>
        </ul>
      </li>
      <li>
        For Brokerage Advantage, calculations are based on the IRS Pub 590-B
        Table III (Uniform Lifetime) as beneficiary information is not currently
        included.
      </li>
      <li>
        If you have multiple IRAs, you must calculate the appropriate RMD for
        each one. However, the total distribution amount can then be taken from
        any one or more IRAs to satisfy the required amount.
      </li>
      <li>
        IRS rules require the first dollars distributed (with few exceptions
        such as corrective distributions), during the RMD year, be used to
        satisfy the RMD for that year. Additionally, RMDs are not eligible for
        rollover treatment, including Roth conversions.
      </li>
    </ul>

    <div className="rmd-disclosure-title">Not included:</div>
    <ul className="rmd-disclosure-events-list" id="last-list">
      <li>
        RMDs required after death of the participant (Inherited IRAs and
        Inherited Roth IRAs)
      </li>
      <li>
        <b>
          RMDs from any employer-sponsored retirement plan accounts you may have
          with T. Rowe Price or at other financial institutions.
        </b>{' '}
        If you are a participant in 401(k), profit-sharing, 403(b), or other
        defined contribution plans, you may not be required to take an RMD from
        accounts until April 1 following the later of the calendar year in which
        you reach RMD age or retire. Please check with your plan administrator
        for RMD requirements. You must calculate the appropriate RMD for each
        retirement plan (ERISA 403(b), pension and profit-sharing (including
        401(k)) and the RMD must be satisfied from that specific plan.
      </li>
      <li>
        In-kind distributions from a brokerage IRA account to a brokerage
        nonretirement account are not included in the Amount Distributed to Date
        shown above (Brokerage Advantage accounts are not included in this
        limitation).
      </li>
    </ul>

    <div className="close-button-container">
      <Button
        id="rmd-disclosure-close-button"
        buttonType={ButtonType.PRIMARY}
        className={`btn-trp-primary rmd-disclosure-close-button`}
        onClick={closeRMDDisclosure}
      >
        Close
      </Button>
    </div>
  </div>
);

export default RMDDisclosure;
